/* ==========================================================================
 * Stock Guard for PetMax.ca (Concept theme)
 * --------------------------------------------------------------------------
 * Cases ("12 Pack", "24 Pack") and singles ("Individual") of the same product
 * and size are one physical stock: Easify keeps the two counts in sync, but
 * Shopify only validates each variant on its own. Without this guard a
 * shopper can put 2 x 24 Pack AND 48 x Individual of a 48-can stock in the
 * cart and check out.
 *
 * The guard sums units across the "family" (product + size) and blocks:
 *   1. Add to cart  - every product form (product page, sticky bar, quick
 *                     view, quick add). The message shows in the form's
 *                     existing error slot.
 *   2. Cart edits   - quantity changes in the cart drawer and cart page.
 *   3. Checkout     - if the cart is over (stock dropped after adding, JS
 *                     bypassed...), the checkout button is disabled, express
 *                     buttons are hidden and a notice names the item.
 *
 * It only acts when a family is MIXED (two or more variants of the same
 * family involved). A single variant over its own stock is left to
 * Shopify's own validation, which already handles that case.
 *
 * Data (all rendered by Liquid, nothing hard-coded here):
 *   - sections/stock-guard-data.liquid      fetched on demand through the
 *     Section Rendering API: /products/<handle>?section_id=stock-guard-data
 *   - snippets/stock-guard-cart-json.liquid embedded in the cart drawer and
 *     cart page; the theme re-renders both on every cart change
 *   - snippets/stock-guard-form-url.liquid  marks guarded product forms with
 *     data-stock-guard-url (empty for products without a pack family)
 * Wording lives in locales/en.default.json under "stock_guard" (keys end in
 * _html so Liquid does not HTML-escape apostrophes) and reaches this file
 * through theme.stockGuardStrings (snippets/js-variables.liquid).
 *
 * Debug in the browser console:  StockGuard.debug(true)
 * ========================================================================== */
(function (root) {
  'use strict';

  var SECTION_ID = 'stock-guard-data';
  var PRODUCT_CACHE_MS = 10000;
  var VALIDITY_RESET_MS = 6000;
  var debug = false;

  function log() {
    if (!debug || typeof console === 'undefined') return;
    console.log.apply(console, ['[StockGuard]'].concat(Array.prototype.slice.call(arguments)));
  }

  /* ------------------------------------------------------------------ */
  /* Pure logic (no DOM). Exposed as window.StockGuard for testing.      */
  /* ------------------------------------------------------------------ */

  function toInt(value) {
    var n = parseInt(value, 10);
    return isFinite(n) ? n : null;
  }

  // "Individual" -> 1, "12 Pack" -> 12, "24pk" -> 24, "Case of 24" -> 24, otherwise null.
  function parsePackValue(value) {
    if (value === null || value === undefined) return null;
    var s = String(value).trim();
    if (!s) return null;
    if (/^(individual|single|singles|each)$/i.test(s)) return 1;
    var m = s.match(/\b(?:pack|case)\s*of\s*(\d+)\b/i);
    if (m) return toInt(m[1]);
    m = s.match(/\b(\d+)\s*-?\s*(?:pack|pk|pks|case)\b/i);
    if (m) return toInt(m[1]);
    return null;
  }

  // "00546(12)" -> 12. Only used when the option value gives no pack size.
  function parseSkuPack(sku) {
    var m = String(sku || '').match(/\((\d+)\)\s*$/);
    return m ? toInt(m[1]) : null;
  }

  // Accepts the compact shape from stock-guard-product-json.liquid (o1/iq/im/ip)
  // or a raw Shopify variant JSON (option1/inventory_quantity/...).
  function normalizeVariant(v) {
    return {
      id: Number(v.id),
      opts: [
        v.o1 !== undefined ? v.o1 : v.option1,
        v.o2 !== undefined ? v.o2 : v.option2,
        v.o3 !== undefined ? v.o3 : v.option3
      ],
      sku: v.sku || '',
      iq: v.iq !== undefined ? v.iq : v.inventory_quantity,
      im: v.im !== undefined ? v.im : v.inventory_management,
      ip: v.ip !== undefined ? v.ip : v.inventory_policy
    };
  }

  // Groups a product's variants into families (same product + same size).
  // Each family knows its base (pack of 1) variant and the shared ceiling.
  function analyzeProduct(product) {
    var result = { id: Number(product.id), title: product.title || '', byVariant: {}, families: {} };
    var variants = (product.variants || []).map(normalizeVariant);
    if (variants.length < 2) return result;

    // Which option carries the pack size? Option 2 first, then option 3.
    // Option 1 is the size or flavour and is never treated as the pack.
    var packIndex = -1;
    for (var idx = 1; idx <= 2 && packIndex === -1; idx++) {
      for (var i = 0; i < variants.length; i++) {
        var pack = parsePackValue(variants[i].opts[idx]);
        if (pack !== null && pack > 1) { packIndex = idx; break; }
      }
    }
    if (packIndex === -1) return result;

    variants.forEach(function (v) {
      var pack = parsePackValue(v.opts[packIndex]);
      if (pack === null) pack = parseSkuPack(v.sku);
      if (pack === null) return;
      var keyParts = [];
      v.opts.forEach(function (value, i) { if (i !== packIndex && value) keyParts.push(String(value)); });
      var key = keyParts.join(' / ');
      var member = { id: v.id, pack: pack, label: String(v.opts[packIndex] || ''), familyKey: key, iq: v.iq, im: v.im, ip: v.ip };
      result.byVariant[v.id] = member;
      if (!result.families[key]) {
        result.families[key] = { key: key, size: key, members: [], base: null, ceiling: null, guarded: false };
      }
      result.families[key].members.push(member);
    });

    Object.keys(result.families).forEach(function (key) {
      var family = result.families[key];
      var base = null;
      var hasPack = false;
      family.members.forEach(function (m) {
        if (m.pack === 1 && !base) base = m;
        if (m.pack > 1) hasPack = true;
      });
      family.base = base;
      // Only guard when Shopify tracks the base stock and does not allow overselling.
      if (base && hasPack && base.im === 'shopify' && base.ip !== 'continue' && typeof base.iq === 'number') {
        family.ceiling = base.iq;
        family.guarded = true;
      }
    });

    return result;
  }

  function familyLines(analysis, family, lines) {
    var out = [];
    lines.forEach(function (line) {
      var member = analysis.byVariant[Number(line.vid)];
      if (!member || member.familyKey !== family.key) return;
      out.push({ key: line.key, vid: Number(line.vid), qty: Number(line.qty), pack: member.pack, label: member.label, units: Number(line.qty) * member.pack });
    });
    return out;
  }

  function sumUnits(lines) {
    return lines.reduce(function (sum, line) { return sum + line.units; }, 0);
  }

  function productLines(lines, productId) {
    return (lines || []).filter(function (line) { return Number(line.pid) === Number(productId); });
  }

  function findLine(state, key) {
    var found = null;
    (state.items || []).forEach(function (line) { if (line.key === key) found = line; });
    return found;
  }

  // Replaces [token] placeholders (the locale strings use {{ token }} and
  // js-variables.liquid turns them into [token]).
  function fmt(template, vars) {
    return String(template || '').replace(/\[(\w+)\]/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : match;
    });
  }

  // "2 x 24 Pack and 20 x Individual" (largest pack first).
  function describeLines(lines, strings) {
    var sorted = lines.slice().sort(function (a, b) { return b.pack - a.pack; });
    var parts = sorted.map(function (line) { return fmt(strings.inCartLine, { quantity: line.qty, option: line.label }); });
    if (parts.length <= 1) return parts.join('');
    return parts.slice(0, -1).join(strings.listSeparator) + strings.listLastSeparator + parts[parts.length - 1];
  }

  // Adding `quantity` of `variantId` to a cart that holds `cartLines`.
  function checkAdd(product, cartLines, variantId, quantity, strings) {
    var analysis = analyzeProduct(product);
    var member = analysis.byVariant[Number(variantId)];
    if (!member) return { ok: true, reason: 'not-in-family' };
    var family = analysis.families[member.familyKey];
    if (!family || !family.guarded) return { ok: true, reason: 'not-guarded' };

    var lines = familyLines(analysis, family, productLines(cartLines, analysis.id));
    var otherUnits = sumUnits(lines.filter(function (line) { return line.vid !== member.id; }));
    if (otherUnits === 0) return { ok: true, reason: 'single-variant' };

    var used = sumUnits(lines);
    var total = used + quantity * member.pack;
    if (total <= family.ceiling) return { ok: true, reason: 'fits' };

    var remaining = Math.max(0, family.ceiling - used);
    var vars = {
      title: analysis.title,
      size: family.size,
      option: member.label,
      in_cart: describeLines(lines, strings),
      remaining: remaining,
      remaining_of_option: Math.floor(remaining / member.pack)
    };
    var template = remaining <= 0 ? strings.addNoneLeft : (remaining < member.pack ? strings.addNotEnoughForPack : strings.addPartial);
    return { ok: false, message: fmt(template, vars), vars: vars };
  }

  // Changing cart line `lineKey` to `newQty`. `state` is the embedded cart JSON.
  function checkChange(state, lineKey, newQty, strings) {
    var line = findLine(state, lineKey);
    if (!line) return { ok: true, reason: 'no-line' };
    var product = state.products[String(line.pid)];
    if (!product) return { ok: true, reason: 'no-product' };
    var analysis = analyzeProduct(product);
    var member = analysis.byVariant[Number(line.vid)];
    if (!member) return { ok: true, reason: 'not-in-family' };
    var family = analysis.families[member.familyKey];
    if (!family || !family.guarded) return { ok: true, reason: 'not-guarded' };

    var lines = familyLines(analysis, family, productLines(state.items, analysis.id));
    var others = lines.filter(function (l) { return l.key !== lineKey; });
    var otherUnits = sumUnits(others);
    if (otherUnits === 0) return { ok: true, reason: 'single-variant' };

    var total = otherUnits + newQty * member.pack;
    if (total <= family.ceiling) return { ok: true, reason: 'fits' };

    var vars = {
      title: analysis.title,
      size: family.size,
      option: member.label,
      in_cart: describeLines(lines, strings),
      other_lines: describeLines(others, strings),
      remaining_of_option: Math.max(0, Math.floor((family.ceiling - otherUnits) / member.pack))
    };
    return { ok: false, message: fmt(strings.cartChange, vars), vars: vars };
  }

  // Whole-cart check used by the checkout gate.
  function evaluateCart(state, strings) {
    var violations = [];
    Object.keys(state.products || {}).forEach(function (pid) {
      var analysis = analyzeProduct(state.products[pid]);
      var lines = productLines(state.items, pid);
      Object.keys(analysis.families).forEach(function (key) {
        var family = analysis.families[key];
        if (!family.guarded) return;
        var famLines = familyLines(analysis, family, lines);
        var distinct = {};
        famLines.forEach(function (l) { distinct[l.vid] = true; });
        if (Object.keys(distinct).length < 2) return; // single variant: Shopify handles it
        var used = sumUnits(famLines);
        if (used <= family.ceiling) return;
        var vars = { title: analysis.title, size: family.size, in_cart: describeLines(famLines, strings), over_by: used - family.ceiling };
        violations.push({ message: fmt(strings.checkoutBlocked, vars), vars: vars, family: family, lines: famLines });
      });
    });
    return violations;
  }

  var DEFAULT_STRINGS = {
    addNoneLeft: 'Your cart already includes everything we currently have in stock for this item ([size]), counting both cases and singles. To swap cases for singles (or the other way around), adjust the quantities in your cart.',
    addNotEnoughForPack: "There isn't enough of this item ([size]) left in stock to add a [option]. Cases and singles come from the same stock, and your cart already includes [in_cart].",
    addPartial: "We don't have enough of this item ([size]) in stock to add that many. Cases and singles come from the same stock, and your cart already includes [in_cart]. Please try a smaller quantity.",
    cartChange: "We don't have enough of this item ([size]) in stock for that quantity. Cases and singles come from the same stock, and your cart also includes [other_lines]. Reduce another line to make room, or choose a smaller quantity.",
    checkoutBlocked: '[title] ([size]): your cart has more cases and singles of this item than we currently have in stock ([in_cart]). Please reduce the quantity to continue to checkout.',
    inCartLine: '[quantity] × [option]',
    listSeparator: ', ',
    listLastSeparator: ' and '
  };

  // Liquid's translation filter HTML-escapes strings whose key does not end in
  // _html (so "don't" arrives as "don&#39;t"). The keys use the _html suffix,
  // but decode anyway in case a string is edited without it.
  var entityDecoder = null;
  function decodeEntities(text) {
    if (typeof document === 'undefined' || text.indexOf('&') === -1) return text;
    entityDecoder = entityDecoder || document.createElement('textarea');
    entityDecoder.innerHTML = text;
    return entityDecoder.value;
  }

  function getStrings() {
    var theme = root.theme || {};
    var custom = theme.stockGuardStrings || {};
    var out = {};
    Object.keys(DEFAULT_STRINGS).forEach(function (key) {
      var value = (typeof custom[key] === 'string' && custom[key] !== '') ? custom[key] : DEFAULT_STRINGS[key];
      out[key] = decodeEntities(value);
    });
    return out;
  }

  var api = {
    parsePackValue: parsePackValue,
    parseSkuPack: parseSkuPack,
    analyzeProduct: analyzeProduct,
    checkAdd: checkAdd,
    checkChange: checkChange,
    evaluateCart: evaluateCart,
    format: fmt,
    defaultStrings: DEFAULT_STRINGS,
    debug: function (on) { debug = on !== false; return debug; }
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof document === 'undefined') return;
  root.StockGuard = api;

  /* ------------------------------------------------------------------ */
  /* Storefront wiring                                                   */
  /* ------------------------------------------------------------------ */

  function routes() {
    return (root.theme && root.theme.routes) || {};
  }

  function handleFromUrl(url) {
    var m = String(url || '').match(/\/products\/([^\/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  function fetchCartLines() {
    var cartUrl = routes().cart_url || '/cart';
    return fetch(cartUrl + '.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        return (cart.items || []).map(function (item) {
          return { key: item.key, vid: Number(item.variant_id), pid: Number(item.product_id), qty: Number(item.quantity), handle: item.handle };
        });
      });
  }

  var productCache = {};

  function fetchProduct(productUrl) {
    var base = String(productUrl).split('#')[0].split('?')[0];
    var cached = productCache[base];
    if (cached && Date.now() - cached.time < PRODUCT_CACHE_MS) return Promise.resolve(cached.data);
    return fetch(base + '?section_id=' + SECTION_ID, { credentials: 'same-origin', cache: 'no-store' })
      .then(function (response) {
        if (!response.ok) throw new Error('stock-guard-data responded ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.querySelector('script[data-stock-guard-product]');
        if (!node) throw new Error('stock-guard-data section not found in response');
        var data = JSON.parse(node.textContent);
        productCache[base] = { time: Date.now(), data: data };
        return data;
      });
  }

  function readCartState(scope) {
    var node = scope && scope.querySelector ? scope.querySelector('script[data-stock-guard-cart]') : null;
    if (!node) return null;
    try {
      var state = JSON.parse(node.textContent);
      state.items = state.items || [];
      state.products = state.products || {};
      return state;
    } catch (error) {
      log('could not parse cart data', error);
      return null;
    }
  }

  function sectionOf(element) {
    return (element && element.closest && element.closest('.shopify-section')) || document;
  }

  function isInViewport(element) {
    var rect = element.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < (root.innerHeight || document.documentElement.clientHeight);
  }

  /* --- 1. Add to cart -------------------------------------------------- */

  function productUrlForForm(form) {
    if (form.hasAttribute('data-stock-guard-url')) return form.getAttribute('data-stock-guard-url') || null;
    var info = form.closest('product-info');
    return info ? info.getAttribute('data-product-url') : null;
  }

  function isProductForm(form) {
    return form.getAttribute('is') === 'product-form' || /\/cart\/add/.test(form.getAttribute('action') || '');
  }

  function onSubmitCapture(event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement) || !isProductForm(form)) return;
    if (form.__stockGuardPassed) return;              // our own re-submit after a successful check
    var url = productUrlForForm(form);
    if (!url) return;                                  // not a guarded product

    if (form.__stockGuardPending) {                    // ignore double clicks while checking
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    var data = new FormData(form);
    var variantId = toInt(data.get('id'));
    var quantity = toInt(data.get('quantity')) || 1;
    if (!variantId) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    var submitter = event.submitter || null;
    form.__stockGuardPending = true;
    if (submitter) submitter.setAttribute('aria-busy', 'true');
    setFormError(form, false);

    runAddCheck(url, variantId, quantity)
      .catch(function (error) {
        log('check failed, letting the add through', error);
        return { ok: true, reason: 'error' };
      })
      .then(function (verdict) {
        form.__stockGuardPending = false;
        if (submitter) submitter.removeAttribute('aria-busy');
        log('add', variantId, 'x', quantity, verdict);
        if (verdict.ok) {
          resubmit(form, submitter);
        } else {
          setFormError(form, verdict.message);
        }
      });
  }

  function runAddCheck(url, variantId, quantity) {
    var handle = handleFromUrl(url);
    return fetchCartLines().then(function (lines) {
      // Fresh stock is only fetched when another variant of this product is already in the cart.
      var siblings = lines.filter(function (line) { return line.handle === handle && line.vid !== variantId; });
      if (!siblings.length) return { ok: true, reason: 'no-siblings-in-cart' };
      return fetchProduct(url).then(function (product) {
        return checkAdd(product, lines, variantId, quantity, getStrings());
      });
    });
  }

  function resubmit(form, submitter) {
    form.__stockGuardPassed = true;
    try {
      if (typeof form.requestSubmit === 'function') {
        if (submitter && submitter.form === form) form.requestSubmit(submitter);
        else form.requestSubmit();
      } else {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    } finally {
      form.__stockGuardPassed = false;
    }
  }

  function setFormError(form, message) {
    var handled = false;
    if (typeof form.handleErrorMessage === 'function' && form.querySelector('.product-form__error-message')) {
      form.handleErrorMessage(message);
      handled = true;
    }
    // Mirror into the sticky add-to-cart bar when it drives this form.
    if (form.id) {
      document.querySelectorAll('product-sticky-form[form="' + form.id + '"]').forEach(function (sticky) {
        if (typeof sticky.handleErrorMessage !== 'function') return;
        sticky.handleErrorMessage(message);
        if (sticky.querySelector('.product-form__error-message')) handled = true;
      });
    }
    if (message === false) { hideToast(); return; }
    if (handled) {
      var slot = form.querySelector('.product-form__error-message');
      if (slot && !slot.hidden && typeof slot.scrollIntoView === 'function' && !isInViewport(slot)) slot.scrollIntoView({ block: 'center', behavior: 'smooth' });
    } else {
      showToast(message);
    }
  }

  var toastEl = null;
  var toastTimer = null;

  function showToast(message) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'stock-guard-toast';
      toastEl.setAttribute('role', 'alert');
      toastEl.style.cssText = 'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);max-width:min(560px,calc(100vw - 32px));padding:14px 18px;background:#111;color:#fff;font-size:14px;line-height:1.4;border-radius:6px;box-shadow:0 8px 24px rgba(0,0,0,.25);z-index:2147483000;';
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    toastEl.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 9000);
  }

  function hideToast() {
    if (toastEl) toastEl.hidden = true;
  }

  /* --- 2. Cart quantity changes --------------------------------------- */

  function onChangeCapture(event) {
    var input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.classList.contains('quantity__input')) return;
    var cartItems = input.closest('cart-items');
    if (!cartItems) return;
    var key = input.getAttribute('data-index');
    if (!key) return;
    var state = readCartState(cartItems);
    if (!state) return;
    var line = findLine(state, key);
    if (!line) return;

    var newQty = toInt(input.value);
    if (newQty === null || newQty <= Number(line.qty)) { clearValidity(input); return; } // decreases are always fine

    var verdict = checkChange(state, key, newQty, getStrings());
    log('change', key, line.qty, '->', newQty, verdict);
    if (verdict.ok) { clearValidity(input); return; }

    event.preventDefault();
    event.stopImmediatePropagation();
    input.value = String(line.qty);
    input.setCustomValidity(verdict.message);
    input.reportValidity();
    input.__stockGuardInvalid = true;
    setTimeout(function () { clearValidity(input); }, VALIDITY_RESET_MS);
    showNotice(sectionOf(cartItems), [verdict.message], true);
  }

  function clearValidity(input) {
    if (input.__stockGuardInvalid) {
      input.__stockGuardInvalid = false;
      input.setCustomValidity('');
    }
  }

  /* --- 3. Checkout gate ------------------------------------------------ */

  function renderNotice(notice, messages) {
    if (!notice) return;
    notice.textContent = '';
    messages.forEach(function (message) {
      var p = document.createElement('p');
      p.textContent = message;
      notice.appendChild(p);
    });
    notice.hidden = messages.length === 0;
  }

  function showNotice(section, messages, transient) {
    var notice = section.querySelector('[data-stock-guard-notice]');
    if (!notice) return;
    renderNotice(notice, messages);
    notice.__stockGuardTransientUntil = transient ? Date.now() + VALIDITY_RESET_MS : 0;
  }

  function evaluateSection(section) {
    var state = readCartState(section);
    var violations = state ? evaluateCart(state, getStrings()) : [];
    var notice = section.querySelector('[data-stock-guard-notice]');
    var buttons = section.querySelectorAll('button[name="checkout"]');
    var express = section.querySelectorAll('.additional-checkout-buttons');

    if (violations.length) {
      renderNotice(notice, violations.map(function (v) { return v.message; }));
      if (notice) notice.__stockGuardTransientUntil = 0;
      buttons.forEach(function (button) {
        button.setAttribute('disabled', '');
        button.setAttribute('data-stock-guard-disabled', '');
      });
      express.forEach(function (el) {
        el.hidden = true;
        el.setAttribute('data-stock-guard-hidden', '');
      });
    } else {
      if (notice && !(notice.__stockGuardTransientUntil > Date.now())) {
        notice.hidden = true;
        notice.textContent = '';
      }
      buttons.forEach(function (button) {
        if (!button.hasAttribute('data-stock-guard-disabled')) return;
        button.removeAttribute('disabled');
        button.removeAttribute('data-stock-guard-disabled');
      });
      express.forEach(function (el) {
        if (!el.hasAttribute('data-stock-guard-hidden')) return;
        el.hidden = false;
        el.removeAttribute('data-stock-guard-hidden');
      });
    }
    return violations;
  }

  function evaluateAll() {
    var seen = [];
    document.querySelectorAll('script[data-stock-guard-cart]').forEach(function (node) {
      var section = sectionOf(node);
      if (seen.indexOf(section) !== -1) return;
      seen.push(section);
      evaluateSection(section);
    });
  }

  var evaluateTimer = null;

  function scheduleEvaluate() {
    clearTimeout(evaluateTimer);
    evaluateTimer = setTimeout(evaluateAll, 80);
  }

  function onCheckoutClickCapture(event) {
    var target = event.target;
    var button = target && target.closest ? target.closest('button[name="checkout"]') : null;
    if (!button) return;
    var section = sectionOf(button);
    var violations = evaluateSection(section);
    log('checkout click', violations);
    if (!violations.length) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    var notice = section.querySelector('[data-stock-guard-notice]');
    if (notice && typeof notice.scrollIntoView === 'function') notice.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  function isCartMutation(mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var target = mutations[i].target;
      if (target && !target.closest) target = target.parentElement;
      if (!target || !target.closest) continue;
      if (target.closest('[data-stock-guard-notice]')) continue; // our own notice updates
      if (target.closest('cart-drawer, main-cart, .cart, [id^="MainCart-"], [id^="MiniCart-"]')) return true;
    }
    return false;
  }

  function init() {
    document.addEventListener('submit', onSubmitCapture, true);
    document.addEventListener('change', onChangeCapture, true);
    document.addEventListener('click', onCheckoutClickCapture, true);
    document.addEventListener('cart:updated', scheduleEvaluate);
    document.addEventListener('cart:refresh', scheduleEvaluate);
    if (typeof MutationObserver === 'function') {
      new MutationObserver(function (mutations) {
        if (isCartMutation(mutations)) scheduleEvaluate();
      }).observe(document.body, { childList: true, subtree: true });
    }
    scheduleEvaluate();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})(typeof window !== 'undefined' ? window : globalThis);
